import { useState, type ReactElement } from 'react';

import { dataTypeLabel } from '@/components/documents/custom-field-format';

import { ENTITY_COPY } from '../copy';
import { reminderText } from '../custom-field-form';
import type { CustomFieldDefinition } from '../types';
import { filterByStatus, type UseCatalogView } from '../use-catalog-view';
import { useCatalogRows } from '../use-catalogs-admin';
import { useToggleActive } from '../use-toggle-active';
import { CatalogBody } from './CatalogBody';
import { CatalogToolbar } from './CatalogToolbar';
import { CustomFieldModal } from './CustomFieldModal';
import { ActiveBadge, RowActions } from './RowActions';

const COPY = ENTITY_COPY.custom_field;

interface Props {
  view: UseCatalogView;
  creating: boolean;
  onCreate: () => void;
  onCloseCreate: () => void;
}

/** Campos propios de los documentos (QA F1 #14): nombre, tipo, opciones y si es
 *  obligatorio. La clave interna no se muestra (UX-COPY-01). */
export function CustomFieldsPanel({
  view,
  creating,
  onCreate,
  onCloseCreate,
}: Props): ReactElement {
  const query = useCatalogRows('custom-field-definitions', { enabled: true });
  const toggle = useToggleActive('custom-field-definitions', 'custom_field', (r) => r.label);
  const [editing, setEditing] = useState<CustomFieldDefinition | null>(null);
  const { status } = view.view;
  const rows = filterByStatus(query.data ?? [], status).sort((a, b) =>
    a.label.localeCompare(b.label, 'es'),
  );

  return (
    <>
      <CatalogToolbar status={status} onStatus={view.setStatus} />
      <CatalogBody
        copy={COPY}
        query={query}
        visible={rows.length}
        status={status}
        extraFilter={false}
        onCreate={onCreate}
        onClearFilters={() => view.setStatus('todos')}
      >
        <div className="t-scroll">
          <table className="t t-cards">
            <caption className="sr-only">Campos propios de los documentos</caption>
            <thead>
              <tr>
                <th scope="col">Campo</th>
                <th scope="col">Tipo</th>
                <th scope="col">Opciones</th>
                <th scope="col">Uso</th>
                <th scope="col">Estado</th>
                <th scope="col" className="t-act">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id} aria-label={f.label}>
                  <td data-label="Campo">{f.label}</td>
                  <td data-label="Tipo">{dataTypeLabel(f.dataType)}</td>
                  <td data-label="Opciones">
                    {f.options?.join(' · ') ?? reminderText(f.reminderDays, f.notifyOverdue) ?? '—'}
                  </td>
                  <td data-label="Uso">
                    <span className={`badge ${f.required ? 'b-warn' : 'b-mut'}`}>
                      {f.required ? 'Obligatorio' : 'Opcional'}
                    </span>
                  </td>
                  <td data-label="Estado">
                    <ActiveBadge active={f.isActive} />
                  </td>
                  <td data-label="Acciones" className="t-act">
                    <RowActions
                      label={f.label}
                      active={f.isActive}
                      busy={toggle.isBusy(f.id)}
                      onEdit={() => setEditing(f)}
                      onToggle={() => toggle.request(f)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CatalogBody>
      <CustomFieldModal
        open={creating || editing !== null}
        editing={editing}
        onClose={() => {
          setEditing(null);
          onCloseCreate();
        }}
      />
      {toggle.confirm}
    </>
  );
}
